import type { CSSProperties, FormHTMLAttributes, ReactNode } from 'react';
import { usePanelChrome } from './chrome/PanelChromeContext';

export interface FormProps extends FormHTMLAttributes<HTMLFormElement> {
  children: ReactNode;
}

function FormRoot({ children, style, ...rest }: FormProps) {
  return (
    <form
      {...rest}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        ...style,
      }}
    >
      {children}
    </form>
  );
}

export interface FormFieldProps {
  label: string;
  htmlFor: string;
  required?: boolean;
  helperText?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}

const labelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--dg-text-2, #334155)',
};

const helperStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--dg-text-3, #64748b)',
  margin: 0,
};

const errorStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--dg-danger, #dc2626)',
  margin: 0,
};

const asteriskStyle: CSSProperties = {
  marginLeft: 4,
  color: 'var(--dg-danger, #dc2626)',
};

function FormField({ label, htmlFor, required, helperText, error, children }: FormFieldProps) {
  const showError = error != null && error !== '';
  const helperId = `${htmlFor}-helper`;
  const errorId = `${htmlFor}-error`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor={htmlFor} style={labelStyle}>
        {label}
        {required && (
          <span style={asteriskStyle} aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {showError ? (
        <p id={errorId} style={errorStyle}>
          {error}
        </p>
      ) : helperText ? (
        <p id={helperId} style={helperStyle}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
}

export interface FormRowProps {
  children: ReactNode;
}

function FormRow({ children }: FormRowProps) {
  const { isMobile } = usePanelChrome();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: 16,
      }}
    >
      {children}
    </div>
  );
}

export const Form = Object.assign(FormRoot, {
  Field: FormField,
  Row: FormRow,
});
